import { memo } from "react";
import { AppButton, Typography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { EChapterIcon } from "@libs/zapi-ui/const";
import { ChapterIcon } from "./ChapterIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useParams } from "react-router-dom";

type SidebarChapterItemProps = {
  isActive: boolean;
  isExpanded: boolean;
  activeSubsection: string | null;
  handleChapterChange: (id: string) => void;
  handleSubsectionClick: (chapterId: string, subsection: string) => void;
  isSidebarOpen: boolean;
  hasActiveMemo: boolean;
  isPolling: boolean;
  hasNotification?: boolean;
  chapter: {
    id: string;
    title: string;
    icon: EChapterIcon | null;
    completenessRatio: number;
    subsections: string[];
    totalQuestions: number;
    validQuestionsCount: number;
    isHidden: boolean;
  };
};

export const SidebarChapterItem = memo((props: SidebarChapterItemProps) => {
  const {
    chapter,
    isActive,
    isExpanded,
    isSidebarOpen,
    activeSubsection,
    handleChapterChange,
    handleSubsectionClick,
    hasActiveMemo,
    isPolling,
    hasNotification = false
  } = props;

  const { getLoadingState } = useChatStore();

  const { id: projectId } = useParams() as { id: string };
  const { pendingGenerationId, isGeneratingChapter, isChapterLoaded } = getLoadingState(projectId, chapter.id);
  const isChatLoading = (!!pendingGenerationId || isGeneratingChapter) && !isChapterLoaded;

  return (
    <Tooltip open={!isSidebarOpen ? undefined : false}>
      <TooltipTrigger asChild>
        <div data-testid={`sidebar-chapter-${chapter.id}`} className="w-full flex flex-col items-center justify-center">
          <div
            className={cn(
              "w-full flex justify-start items-center cursor-pointer group",
              isSidebarOpen && isExpanded && chapter.subsections.length > 0 && "mb-3"
            )}
            onClick={() => handleChapterChange(chapter.id)}
            data-testid={`sidebar-chapter-${chapter.id}-header`}
          >
            <ChapterIcon
              validQuestionsCount={chapter.validQuestionsCount}
              totalQuestions={chapter.totalQuestions}
              isActive={isActive}
              chapterIcon={chapter.icon}
              testId={`sidebar-chapter-${chapter.id}-icon`}
              isHidden={chapter.isHidden}
              isLoading={(!hasActiveMemo && isPolling) || isChatLoading}
              hasNotification={hasNotification}
            />

            <Typography
              variant="label2"
              color={isActive ? "zinc900" : "zinc500"}
              className={cn(
                "font-medium max-w-[200px] truncate ml-2 transition-colors duration-300 group-hover:text-innoveraZinc-900"
              )}
              style={{ lineHeight: "120%" }}
              data-testid={`sidebar-chapter-${chapter.id}-title`}
            >
              {chapter.title}
            </Typography>
          </div>
          {chapter.subsections.length > 0 && (
            <div
              className={cn(
                "w-full grid transition-all duration-300 ease-in-out pl-1.5",
                isSidebarOpen && isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              )}
            >
              <div className="w-full flex flex-col items-start pl-4 overflow-hidden">
                {chapter.subsections.map((subsection, subIndex) => (
                  <AppButton
                    key={subIndex}
                    appearance="text"
                    onClick={() => handleSubsectionClick(chapter.id, subsection)}
                    className={cn(
                      "rounded-none border-l-2 border-innoveraZinc-150 pl-7 text-innoveraZinc-500 font-medium",
                      "hover:bg-transparent hover:border-innoveraOrange-300 hover:text-innoveraZinc-900",
                      activeSubsection === subsection && "text-innoveraZinc-900 border-innoveraOrange-400"
                    )}
                    data-testid={`sidebar-chapter-${chapter.id}-subsection-${subIndex}`}
                  >
                    <Typography variant="label2" className="truncate max-w-[200px]" style={{ lineHeight: "120%" }}>
                      {subsection}
                    </Typography>
                  </AppButton>
                ))}
              </div>
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="right">
        <Typography variant="body3">{chapter.title}</Typography>
      </TooltipContent>
    </Tooltip>
  );
});
SidebarChapterItem.displayName = "SidebarChapterItem";
